import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  CreditEngineWhitelistBlacklist,
  WhitelistItem,
  WhitelistStatus,
} from "@/models/consignado/whitelistBlacklist.model";
import { addWhitelistCnpj, deleteWhitelistCnpj, getWhitelistBlacklist } from "@/services/consignado/whitelist-blacklist.service";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { CiSearch, CiShop } from "react-icons/ci";
import * as XLSX from "xlsx";



function formatCnpj(item: WhitelistItem): string {
  const digits = `${item.cnpjBasico}${item.cnpjOrdem}${item.cnpjDv}`;
  if (digits.length !== 14) return digits;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function toDateLabel(date: string): string {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("pt-BR");
}

function normalizeHeader(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function normalizeStatus(value: string): WhitelistStatus | null {
  const normalized = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .trim()
    .toLowerCase();
  
  if (normalized === "aprovado") return "aprovado";
  if (normalized === "reprovado") return "reprovado";
  return null;
}

function parseApiError(error: any): string {
  const raw: string | undefined = error?.response?.data?.message;
  const message = raw ?? (error instanceof Error ? error.message : "Erro desconhecido");
  const cleaned = message.replace(/[A-Za-z]+Exception:\s*/g, "");
  return cleaned
    .replace(/whitelist\/blacklist/gi, "pré-aprovados/pré-reprovados")
    .replace(/whitelist/gi, "pré-aprovados")
    .replace(/blacklist/gi, "pré-reprovados");
}

function statusPillStyle(status: WhitelistStatus) {
  if (status === "aprovado") {
    return {
      bg: "#E8F7EC",
      color: "#1F7A38",
      border: "1px solid #CBEED4",
      label: "PRÉ-APROVADO",
    };
  }

  return {
    bg: "#F8E6E6",
    color: "#9B2C2C",
    border: "1px solid #F2CDCD",
    label: "PRÉ-REPROVADO",
  };
}

export default function WhitelistBlacklistPage() {
  const backgroundStyle = useConditionalBackground();
  const { user } = useEnsureAuthenticated();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState<WhitelistStatus | "">("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [sortBy, setSortBy] = useState<"cnpj" | "companyName" | "status" | "createdAt" | "userId">("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Form states
  const [addTab, setAddTab] = useState<"individual" | "bulk">("individual");
  const [addCnpj, setAddCnpj] = useState("");
  const [addCompanyName, setAddCompanyName] = useState("");
  const [addStatus, setAddStatus] = useState<WhitelistStatus>("aprovado");
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement | null>(null);
  const [, setBulkBusinesses] = useState<Array<CreditEngineWhitelistBlacklist["business"][0]>>([]);
  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const headerColor = useColorModeValue("white", "white");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const rowBorderColor = useColorModeValue("gray.100", "gray.600");

  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ["consignado-whitelist-blacklist", page, pageSize, status, appliedSearch],
    queryFn: async () => {
      const isOnlyDigits = /^[\d.\-\/]+$/.test(appliedSearch.trim());
      return getWhitelistBlacklist({
        page,
        pageSize,
        status: status || undefined,
        cnpj: appliedSearch && isOnlyDigits ? appliedSearch : undefined,
        razaoSocial: appliedSearch && !isOnlyDigits ? appliedSearch : undefined,
        order: "DESC",
      });
    },
    placeholderData: (previousData) => previousData,
  });

  const tableItems = useMemo(() => {
    if (data?.items?.length) {
      return data.items;
    }
    return [];
  }, [data?.items]);

  const statusCounters = useMemo(() => {
    const items = tableItems;
    const approved = items.filter((item) => item.status === "aprovado").length;
    const rejected = items.filter((item) => item.status === "reprovado").length;
    return { approved, rejected };
  }, [tableItems]);

  const sortedData = useMemo(() => {
    const items = [...tableItems];
    const direction = sortDirection === "asc" ? 1 : -1;

    return items.sort((a, b) => {
      if (sortBy === "cnpj") {
        const cnpjA = `${a.cnpjBasico}${a.cnpjOrdem}${a.cnpjDv}`;
        const cnpjB = `${b.cnpjBasico}${b.cnpjOrdem}${b.cnpjDv}`;
        return cnpjA.localeCompare(cnpjB) * direction;
      }

      if (sortBy === "createdAt") {
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * direction;
      }

      if (sortBy === "companyName") {
        return (a.companyName ?? "").localeCompare(b.companyName ?? "") * direction;
      }

      if (sortBy === "userId") {
        return (a.userId ?? "").localeCompare(b.userId ?? "") * direction;
      }

      if (sortBy === "status") {
        return a.status.localeCompare(b.status) * direction;
      }

      return `${a.cnpjBasico}${a.cnpjOrdem}${a.cnpjDv}`.localeCompare(`${b.cnpjBasico}${b.cnpjOrdem}${b.cnpjDv}`) * direction;
    });
  }, [tableItems, sortBy, sortDirection]);

  const totalPages = data?.totalPages ?? Math.max(1, Math.ceil(tableItems.length / pageSize));

  const handleSort = (field: "cnpj" | "companyName" | "status" | "createdAt" | "userId") => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortDirection("asc");
  };

  const handleSearch = () => {
    setPage(1);
    setAppliedSearch(search);
  };

  const handleClear = () => {
    setPage(1);
    setStatus("");
    setSearch("");
    setAppliedSearch("");
  };

  const submitWhitelistRequest = async (
    businesses: Array<CreditEngineWhitelistBlacklist["business"][0]>,
  ) => {
    if (!user?.id) {
      toast({
        title: "Erro",
        description: "Usuário autenticado não encontrado para envio da requisição.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    if (businesses.length === 0) {
      toast({
        title: "Erro",
        description: "Nenhum CNPJ válido foi encontrado para envio.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    const payload: CreditEngineWhitelistBlacklist = {
      status: addStatus,
      userId: user.id,
      business: businesses as CreditEngineWhitelistBlacklist["business"],
    };
    
    await addWhitelistCnpj(payload);
    await refetch();
    return true;
  };

  const handleAddCnpj = async () => {
    const sanitizedCnpj = addCnpj.trim();

    if (!sanitizedCnpj) {
      toast({
        title: "Erro",
        description: "Informe um CNPJ para adicionar.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsAddLoading(true);

    try {
      const success = await submitWhitelistRequest([
        {
          cnpj: sanitizedCnpj,
          companyName: addCompanyName.trim() || undefined,
          status: addStatus,
        },
      ]);

      if (!success) {
        return;
      }

      toast({
        title: "Sucesso",
        description: "CNPJ enviado com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setAddCnpj("");
      setAddCompanyName("");
      setAddStatus("aprovado");
    } catch (error: any) {
      toast({
        title: "Erro ao adicionar CNPJ",
        description: parseApiError(error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAddLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteWhitelistCnpj(id);
      await refetch();
      toast({
        title: "CNPJ excluído",
        description: "CNPJ removido com sucesso.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao excluir",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleBulkImport = async () => {
    setIsBulkLoading(true);
    if (!bulkFile) {
      toast({
        title: "Erro",
        description: "Selecione um arquivo para importar",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const buffer = await bulkFile.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const data: any[] = XLSX.utils.sheet_to_json(worksheet);
      
      if (!data || data.length === 0) {
        toast({
          title: "Erro",
          description: "O arquivo não contém dados",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Validate headers
      const headers = Object.keys(data[0]);
      const normalizedHeaders = headers.reduce<Record<string, string>>((accumulator, header) => {
        accumulator[normalizeHeader(header)] = header;
        return accumulator;
      }, {});
      const cnpjHeader = normalizedHeaders.CNPJ;
      const razaoSocialHeader = normalizedHeaders["RAZAO SOCIAL"];
      const statusHeader = normalizedHeaders.STATUS;

      const errors: string[] = [];

      if (!cnpjHeader) {
        errors.push('Coluna "CNPJ" não encontrada');
      }
      if (!razaoSocialHeader) {
        errors.push('Coluna "RAZAO SOCIAL" não encontrada');
      }
      if (!statusHeader) {
        errors.push('Coluna "STATUS" é obrigatória');
      }

      if (errors.length > 0) {
        toast({
          title: "Erro na estrutura do arquivo",
          description: errors.join(" | "),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Validate data
      const dataErrors: string[] = [];
      data.forEach((row, index) => {
        const cnpjValue = cnpjHeader ? row[cnpjHeader] : "";
        const razaoValue = razaoSocialHeader ? row[razaoSocialHeader] : "";
        const statusRawValue = statusHeader ? row[statusHeader] : "";
        const statusValue = normalizeStatus(String(statusRawValue));

        if (!cnpjValue || String(cnpjValue).trim() === "") {
          dataErrors.push(`Linha ${index + 2}: CNPJ vazio`);
        }
        if (!razaoValue || String(razaoValue).trim() === "") {
          dataErrors.push(`Linha ${index + 2}: RAZAO SOCIAL vazio`);
        }
        if (!statusValue) {
          dataErrors.push(`Linha ${index + 2}: STATUS inválido ou vazio (use 'Aprovado' ou 'Reprovado')`);
        }
      });

      if (dataErrors.length > 0) {
        const errorMessage = dataErrors.slice(0, 5).join(" | ");
        const moreErrors = dataErrors.length > 5 ? ` ... e mais ${dataErrors.length - 5}` : "";
        toast({
          title: "Erro nos dados do arquivo",
          description: errorMessage + moreErrors,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      const businessList: Array<CreditEngineWhitelistBlacklist["business"][0]> = data.map((row) => {
        const statusRawValue = row[statusHeader];
        const normalizedStatusValue = normalizeStatus(String(statusRawValue));
        
        return {
          cnpj: String(cnpjHeader ? row[cnpjHeader] : "").trim(),
          companyName: String(razaoSocialHeader ? row[razaoSocialHeader] : "").trim(),
          status: normalizedStatusValue!,
        };
      });
      // console.log("Validated business list:", businessList);

      setBulkBusinesses(businessList);

      const success = await submitWhitelistRequest(businessList);

      if (!success) {
        return;
      }

      toast({
        title: "Sucesso",
        description: `${businessList.length} registros enviados com sucesso.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setBulkFile(null);
      setBulkBusinesses([]);
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = "";
      }
      setIsBulkLoading(false);
    } catch (error: any) {
      setIsBulkLoading(false);
      toast({
        title: "Erro ao importar arquivo",
        description: parseApiError(error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box padding={{ base: "2", md: "4" }} w="100%">
      <Box display="flex" flexDirection="column" alignItems="center" width="100%" gap={4}>
        <Box
          {...backgroundStyle}
          borderRadius="md"
          p={{ base: 4, md: 6 }}
          color="white"
          boxShadow="md"
          width="100%"
        >
          <Flex
            alignItems={{ base: "flex-start", md: "center" }}
            justifyContent="space-between"
            flexDirection={{ base: "column", md: "row" }}
            gap={4}
          >
            <Flex alignItems="center" gap={3}>
              <CiShop size="2rem" />
              <Box>
                <Text fontSize={{ base: "2xl", md: "4xl" }} fontWeight="bold" fontFamily="Lexend Deca">
                  Aprovação PJ
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }} opacity={0.9}>
                  CNPJs pré-aprovados/pré-reprovados no motor de crédito
                </Text>
              </Box>
            </Flex>

            <HStack spacing={3} align="stretch">
              <Stat bg="whiteAlpha.300" p={3} borderRadius="md" minW="120px">
                <StatNumber fontSize="2xl">{statusCounters.approved}</StatNumber>
                <StatLabel color="white" whiteSpace="nowrap">Pré-Aprovados</StatLabel>
              </Stat>
              <Stat bg="whiteAlpha.300" p={3} borderRadius="md" minW="140px">
                <StatNumber fontSize="2xl">{statusCounters.rejected}</StatNumber>
                <StatLabel color="white" whiteSpace="nowrap">Pré-Reprovados</StatLabel>
              </Stat>
              <Stat bg="whiteAlpha.300" p={3} borderRadius="md" minW="120px">
                <StatNumber fontSize="2xl">{data?.total ?? tableItems.length}</StatNumber>
                <StatLabel color="white" whiteSpace="nowrap">Total</StatLabel>
              </Stat>
            </HStack>
          </Flex>

          <Flex
            mt={4}
            justifyContent={{ base: "space-between", md: "space-between" }}
            alignItems="center"
            flexDirection={{ base: "column", md: "row" }}
            gap={3}
          >
            <InputGroup width={{ base: "100%", md: "30%" }}>
              <InputLeftElement mt={1} pointerEvents="none">
                <CiSearch color="black" size="2rem" />
              </InputLeftElement>
              <Input
                placeholder="CNPJ ou Razão Social..."
                variant="filled"
                height={{ base: "56px", md: "48px" }}
                type="text"
                bg={"white"}
                maxLength={100}
                value={search}
                onChange={(ev) => setSearch(ev.target.value)}
                onKeyDown={(ev) => { if (ev.key === "Enter") handleSearch(); }}
                color="black"
                _focus={{ bg: "white" }}
                _hover={{ bg: "white" }}
                fontFamily="Lexend Deca"
              />
            </InputGroup>

            <Flex align="center" gap={3} ml="auto">
              <Select
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value as WhitelistStatus | "");
                }}
                bg="white"
                color="#1F3A52"
                size="md"
                w={{ base: "100%", md: "220px" }}
                height={{ base: "56px", md: "48px" }}
                fontFamily="Lexend Deca"
                sx={{
                  option: {
                    color: "#1F3A52",
                    backgroundColor: "white",
                  },
                }}
              >
                <option value="">Todos os status</option>
                <option value="aprovado">Pré-Aprovado</option>
                <option value="reprovado">Pré-Reprovado</option>
              </Select>

              <Button
                bg="whiteAlpha.200"
                color="white"
                px={4}
                py={2}
                borderRadius="md"
                fontWeight="bold"
                border="1px solid"
                borderColor="whiteAlpha.300"
                _hover={{ bg: "whiteAlpha.300" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize={{ base: "md", md: "md" }}
                fontFamily="Lexend Deca"
                height={{ base: "56px", md: "48px" }}
                onClick={handleClear}
              >
                Limpar filtros
              </Button>
              <Button
                bg="white"
                color="#1F3A52"
                px={4}
                py={2}
                borderRadius="md"
                fontWeight="bold"
                _hover={{ bg: "gray.100" }}
                fontSize={{ base: "md", md: "md" }}
                fontFamily="Lexend Deca"
                height={{ base: "56px", md: "48px" }}
                onClick={handleSearch}
              >
                Filtrar
              </Button>
            </Flex>
          </Flex>
        </Box>

        <Box
          borderRadius="md"
          border="2px dashed"
          borderColor="gray.300"
          p={{ base: 4, md: 6 }}
          bg="white"
          width="100%"
          boxShadow="sm"
        >
          <Flex alignItems="center" justifyContent="space-between" mb={4}>
            <Flex alignItems="center" gap={2}>
              <Text fontSize="lg" fontWeight="bold"  fontFamily="Lexend Deca">
                ➕ Adicionar CNPJ a pré-aprovados/pré-reprovados
              </Text>
            </Flex>
          </Flex>

          <Flex gap={3} mb={4}>
            <Button
              size="md"
              bg={addTab === "individual" ? "#2b5b83" : "gray.200"}
              color={addTab === "individual" ? "white" : "gray.700"}
              fontFamily="Lexend Deca"
              fontWeight="bold"
              onClick={() => setAddTab("individual")}
              borderRadius="md"
            >
              Adicionar individual
            </Button>
            <Button
              size="md"
              bg={addTab === "bulk" ? "#2b5b83" : "gray.200"}
              color={addTab === "bulk" ? "white" : "gray.700"}
              fontFamily="Lexend Deca"
              fontWeight="bold"
              onClick={() => setAddTab("bulk")}
              borderRadius="md"
            >
              Importar em lote
            </Button>
          </Flex>

          {addTab === "individual" && (
            <Flex
              flexDirection={{ base: "column", md: "row" }}
              gap={3}
              alignItems="flex-end"
            >
              <Box flex={1} minW="150px">
                <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">
                  CNPJ
                </Text>
                <Input
                  placeholder="00.000.000/0001-00"
                  value={addCnpj}
                  onChange={(e) => setAddCnpj(e.target.value)}
                  fontFamily="monospace"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  height="40px"
                />
              </Box>

              <Box flex={1} minW="200px">
                <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">
                  RAZÃO SOCIAL
                </Text>
                <Input
                  placeholder="Nome da empresa (opcional)"
                  value={addCompanyName}
                  onChange={(e) => setAddCompanyName(e.target.value)}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  height="40px"
                  fontFamily="Lexend Deca"
                />
              </Box>

              <Box minW="150px">
                <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">
                  STATUS
                </Text>
                <Select
                  value={addStatus}
                  onChange={(e) => setAddStatus(e.target.value as WhitelistStatus)}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  height="40px"
                  fontFamily="Lexend Deca"
                  sx={{
                    option: {
                      color: "#1F3A52",
                      backgroundColor: "white",
                    },
                  }}
                >
                  <option value="aprovado">Pré-Aprovado</option>
                  <option value="reprovado">Pré-Reprovado</option>
                </Select>
              </Box>

              <Button
                bg="#22C55E"
                color="white"
                fontWeight="bold"
                fontFamily="Lexend Deca"
                _hover={{ bg: "#16A34A" }}
                height="40px"
                px={6}
                isDisabled={isAddLoading}
                isLoading={isAddLoading}
                loadingText="Adicionando..."
                onClick={handleAddCnpj}
              >
                Adicionar
              </Button>
            </Flex>
          )}

          {addTab === "bulk" && (
            <Flex gap={3} alignItems="flex-end" flexWrap="wrap">
              <Box width="100%" flex={1} minW={0}>
                <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">
                  Importar arquivo Excel
                </Text>
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  height="40px"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  fontFamily="Lexend Deca"
                  pt={1}
                  ref={bulkFileInputRef}
                  onChange={(e) => {
                    setBulkFile(e.target.files?.[0] || null);
                    setBulkBusinesses([]);
                  }}
                />
                {/* {bulkBusinesses.length > 0 && (
                  <Text mt={2} fontSize="xs" color="green.600" fontFamily="Lexend Deca">
                    {bulkBusinesses.length} registros enviados, aguarde.
                  </Text>
                )} */}
              </Box>
              <Button
                bg="#22C55E"
                color="white"
                fontWeight="bold"
                fontFamily="Lexend Deca"
                _hover={{ bg: "#16A34A" }}
                _disabled={{ bg: "#ccc", cursor: "not-allowed" }}
                height="40px"
                px={6}
                flexShrink={0}
                isDisabled={!bulkFile || isBulkLoading}
                isLoading={isBulkLoading}
                loadingText="Importando..."
                onClick={handleBulkImport}
              >
                Importar
              </Button>
              <Button
                bg="#2b5b83"
                color="white"
                fontWeight="bold"
                fontFamily="Lexend Deca"
                border="1px solid #2b5b83"
                _hover={{ bg: "gray.100" }}
                height="40px"
                px={6}
                flexShrink={0}
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = "/exemplo-entrada-lote.xlsx";
                  link.download = "exemplo-entrada-lote.xlsx";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                 Baixar exemplo
              </Button>
            </Flex>
          )}
        </Box>

        {(isLoading || isRefetching) && (
          <Flex justify="center" py={6}>
            <Spinner color="primary" />
          </Flex>
        )}

        {!isLoading && sortedData.length !== 0 ? (
          <>
            <Flex justifyContent="center" mt={4} mb={4} alignItems="center" flexWrap="wrap">
              <IconButton
                icon={<ChevronLeftIcon />}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={page === 1}
                mr={2}
                aria-label="Página Anterior"
                size={{ base: "sm", md: "sm" }}
              />
              <Text mx={4}>Página {page} de {totalPages}</Text>
              <IconButton
                icon={<ChevronRightIcon />}
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                isDisabled={page === totalPages}
                ml={2}
                aria-label="Próxima Página"
                size={{ base: "sm", md: "sm" }}
              />
              <Flex alignItems="center" ml={4}>
                <Text mr={2}>Itens por página:</Text>
                <Select
                  width="fit-content"
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                  }}
                  size="sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Select>
              </Flex>
            </Flex>

            <Box overflow={"visible"} w="100%">
              <TableContainer borderRadius="md" boxShadow="md" bg="white">
                <Table size="sm">
                  <Thead {...backgroundStyle} position="sticky" top={0} zIndex={1} boxShadow="md">
                    <Tr>
                      <Th color={headerColor} p={0} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>CNPJ</Text>
                          <IconButton
                            icon={sortBy === "cnpj" && sortDirection === "asc" ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            variant="ghost"
                            size="1"
                            fontSize={"1rem"}
                            borderRadius={"lg"}
                            aria-label="Ordenar por CNPJ"
                            bg={"gray.200"}
                            onClick={() => handleSort("cnpj")}
                          />
                        </HStack>
                      </Th>
                      <Th color={headerColor} p={0} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>RAZAO SOCIAL</Text>
                          <IconButton
                            icon={sortBy === "companyName" && sortDirection === "asc" ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            variant="ghost"
                            size="1"
                            fontSize={"1rem"}
                            borderRadius={"lg"}
                            aria-label="Ordenar por Razao Social"
                            bg={"gray.200"}
                            onClick={() => handleSort("companyName")}
                          />
                        </HStack>
                      </Th>
                      <Th color={headerColor} p={0} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>STATUS</Text>
                          <IconButton
                            icon={sortBy === "status" && sortDirection === "asc" ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            variant="ghost"
                            size="1"
                            fontSize={"1rem"}
                            borderRadius={"lg"}
                            aria-label="Ordenar por Status"
                            bg={"gray.200"}
                            onClick={() => handleSort("status")}
                          />
                        </HStack>
                      </Th>
                      <Th color={headerColor} p={0} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>ADICIONADO EM</Text>
                          <IconButton
                            icon={sortBy === "createdAt" && sortDirection === "asc" ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            variant="ghost"
                            size="1"
                            fontSize={"1rem"}
                            borderRadius={"lg"}
                            aria-label="Ordenar por Data"
                            bg={"gray.200"}
                            onClick={() => handleSort("createdAt")}
                          />
                        </HStack>
                      </Th>
                      <Th color={headerColor} p={0} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>ADICIONADO POR</Text>
                          <IconButton
                            icon={sortBy === "userId" && sortDirection === "asc" ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            variant="ghost"
                            size="1"
                            fontSize={"1rem"}
                            borderRadius={"lg"}
                            aria-label="Ordenar por Adicionado por"
                            bg={"gray.200"}
                            onClick={() => handleSort("userId")}
                          />
                        </HStack>
                      </Th>
                      <Th color={headerColor} p={0} textAlign="center" fontSize="xs" py={5}>
                        <HStack justify="center">
                          <Text>AÇÕES</Text>
                        </HStack>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedData.map((item) => (
                      <Tr
                        key={item.id}
                        _hover={{ bg: rowHoverBg }}
                        transition="all 0.3s ease-in-out"
                        borderBottom="1px solid"
                        borderColor={rowBorderColor}
                      >
                        <Td textAlign="center" fontSize="xs" py={3} whiteSpace="nowrap" fontFamily="monospace">
                          {formatCnpj(item)}
                        </Td>
                        <Td textAlign="center" fontSize="xs" py={3}>{item.companyName ?? "-"}</Td>
                        <Td textAlign="center" fontSize="xs" py={3} whiteSpace="nowrap">
                          <Box
                            as="span"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            px={3}
                            py={1.5}
                            borderRadius="md"
                            bg={statusPillStyle(item.status).bg}
                            color={statusPillStyle(item.status).color}
                            border={statusPillStyle(item.status).border}
                            fontSize="xs"
                            fontWeight="700"
                            letterSpacing="0.08em"
                            textTransform="uppercase"
                            fontFamily="Lexend Deca"
                            whiteSpace="nowrap"
                          >
                            {statusPillStyle(item.status).label}
                          </Box>
                        </Td>
                        <Td textAlign="center" fontSize="xs" py={3}>{toDateLabel(item.createdAt)}</Td>
                        <Td textAlign="center" fontSize="xs" py={3}>{item.userId ?? "-"}</Td>
                        <Td textAlign="center" fontSize={{ base: "lg", md: "sm" }} py={{ base: 2, md: 3 }} fontFamily="Lexend Deca">
                          <Button
                            size="md"
                            colorScheme="red"
                            width={{ base: "100%", md: "170px" }}
                            py={2}
                            fontSize="sm"
                            fontWeight="bold"
                            fontFamily="Lexend Deca"
                            isLoading={deletingId === item.id}
                            isDisabled={deletingId !== null}
                            loadingText="Excluindo..."
                            onClick={() => handleDelete(item.id)}
                          >
                            Excluir CNPJ
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            <Flex justifyContent="center" mt={4} mb={4} alignItems="center" flexWrap="wrap">
              <IconButton
                icon={<ChevronLeftIcon />}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={page === 1}
                mr={2}
                aria-label="Página Anterior"
                size={{ base: "sm", md: "sm" }}
              />
              <Text mx={4}>Página {page} de {totalPages}</Text>
              <IconButton
                icon={<ChevronRightIcon />}
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                isDisabled={page === totalPages}
                ml={2}
                aria-label="Próxima Página"
                size={{ base: "sm", md: "sm" }}
              />
              <Flex alignItems="center" ml={4}>
                <Text mr={2}>Itens por página:</Text>
                <Select
                  width="fit-content"
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                  }}
                  size="sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Select>
              </Flex>
            </Flex>
          </>
        ) : (
          !isLoading && (
            <Heading mt={20} textAlign={"center"} color="gray.600" size="md">
              Não há CNPJs cadastrados
            </Heading>
          )
        )}
      </Box>
    </Box>
  );
}
